import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'
import { App } from '@capacitor/app'

/**
 * システムのダークモード設定を検出し、ネイティブアプリのStatusBarを自動調整するフック
 * Web版には影響しません（ネイティブアプリのみ動作）
 */
export const useSystemDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(false)
  const isNative = Capacitor.isNativePlatform()

  useEffect(() => {
    // 初回マウント時にシステムのダークモード設定を取得
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    setIsDarkMode(mediaQuery.matches)

    // ネイティブアプリの場合、StatusBarの色を更新
    if (isNative) {
      updateStatusBar(mediaQuery.matches)
    }

    // システムのダークモード設定が変更された時のリスナー
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches)

      // ネイティブアプリの場合、StatusBarの色を更新
      if (isNative) {
        updateStatusBar(e.matches)
      }
    }

    mediaQuery.addEventListener('change', handleChange)

    // アプリがフォアグラウンドに戻った時にもチェック（設定変更に対応）
    if (isNative) {
      let appStateListener: any = null

      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          const currentIsDark = window.matchMedia('(prefers-color-scheme: dark)').matches
          setIsDarkMode(currentIsDark)
          updateStatusBar(currentIsDark)
        }
      }).then(listener => {
        appStateListener = listener
      })

      return () => {
        mediaQuery.removeEventListener('change', handleChange)
        if (appStateListener) {
          appStateListener.remove()
        }
      }
    }

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [isNative])

  return { isDarkMode, isNative }
}

/**
 * StatusBarのスタイルを更新（ネイティブアプリのみ）
 * WebViewをステータスバー領域まで拡張し、CSSのsafe-area-insetで適切なパディングを設定
 */
const updateStatusBar = async (_isDark: boolean) => {
  try {
    // ステータスバーのテキスト/アイコンを白色（Light style = 白いアイコン、紫背景に映える）
    await StatusBar.setStyle({
      style: Style.Light  // Light style = white text/icons (紫背景に映える)
    })

    // ステータスバーをオーバーレイモードに設定（WebViewがステータスバー領域まで拡張）
    // これによりWeb版と同じレイアウトになり、CSSのsafe-area-insetで適切に調整
    await StatusBar.setOverlaysWebView({ overlay: true })

    // ステータスバーの背景色をアプリのブランドカラー（紫）に設定
    // Androidのみ有効（iOSはオーバーレイモードでは背景色は透明）
    await StatusBar.setBackgroundColor({
      color: '#6366F1'  // Indigo-500 - ヘッダーと同じ色
    })
  } catch (error) {
    // Web版やStatusBarがサポートされていない環境ではエラーを無視
    console.log('StatusBar not available:', error)
  }
}
